import { ActionIcon, Group, Menu, Stack, Text, UnstyledButton } from '@mantine/core';
import { ReactNode } from 'react';
import { IconDotsVertical, IconPencil } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import classes from './DocumentCard.module.css';

export type DocumentCardTag = {
  label: string;
  onEdit?: () => void;
  node?: ReactNode;
};

type DocumentCardProps = {
  fileName: string;
  tags: DocumentCardTag[];
  extracting?: boolean;
  highlighted?: boolean;
  menuItems?: ReactNode;
  children?: ReactNode;
};

export default function DocumentCard({
  fileName,
  tags,
  extracting,
  highlighted,
  menuItems,
  children,
}: DocumentCardProps) {
  const border = highlighted
    ? '1.5px solid var(--mantine-color-brand-6)'
    : '1px solid var(--mantine-color-gray-1)';

  return (
    <Stack
      bg="white"
      p="md"
      gap="md"
      w="100%"
      className={extracting ? classes.extracting : undefined}
      style={extracting ? { borderRadius: 20 } : { border, borderRadius: 20 }}
      data-testid="document-card"
    >
      <Group gap="xxs" align="flex-start" wrap="nowrap">
        <Stack gap="xxs" style={{ flex: 1, minWidth: 0 }}>
          <Text size="md" fw={FONT_WEIGHTS.medium} c="gray.10" truncate>
            {fileName}
          </Text>
          {tags.length > 0 && (
            <Group gap="xxxs">
              {tags.map(tag => (
                <TagChip key={tag.label} tag={tag} />
              ))}
            </Group>
          )}
        </Stack>
        {menuItems && (
          <Menu position="bottom-end">
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray.8"
                aria-label="Document actions"
                data-testid="document-card-menu"
              >
                <IconDotsVertical size={20} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>{menuItems}</Menu.Dropdown>
          </Menu>
        )}
      </Group>
      {children && (
        <Stack gap="xxxs" w="100%">
          {children}
        </Stack>
      )}
    </Stack>
  );
}

function TagChip({ tag }: { tag: DocumentCardTag }) {
  if (tag.node) return tag.node;

  if (!tag.onEdit) return <TagChipBox label={tag.label} />;

  return (
    <UnstyledButton onClick={tag.onEdit} aria-label={`Edit ${tag.label}`}>
      <TagChipBox label={tag.label} editable />
    </UnstyledButton>
  );
}

export function TagChipBox({
  label,
  editable,
  active,
}: {
  label: string;
  editable?: boolean;
  active?: boolean;
}) {
  return (
    <Group
      gap="xxxs"
      bg="gray.1"
      px="xxs"
      py="xxxs"
      wrap="nowrap"
      style={{
        borderRadius: 8,
        border: `1px solid ${active ? 'var(--mantine-color-brand-6)' : 'transparent'}`,
      }}
    >
      <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.8">
        {label}
      </Text>
      {editable && <IconPencil size={12} color="var(--mantine-color-gray-8)" />}
    </Group>
  );
}
